import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  validateExamReservationSettings,
  validateExamReservationSettingsAsync,
} from 'components/AppService/ExamV3Settings/ExamReservationSettings/validation';
import _, { get, keyBy, omit, set } from 'lodash';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type { ExamV3ExamReservationConfiguration } from 'types/examV3ExamReservationSettings.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { errorNotification } from 'utils/notifications';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';
import type { z } from 'zod';
import api from '../services/api.service';

export type TExamV3ExamReservationSettingsState = IDefaultReducerState & {
  config: Partial<ExamV3ExamReservationConfiguration> | null;
  errors: Record<string, z.ZodIssue> | null;
};

export const initialState: TExamV3ExamReservationSettingsState = {
  hasErrors: false,
  loading: false,
  config: {
    information: [],
    fields: [],
    seat: {
      enabled: true,
    },
  },
  errors: null,
};

const slice = createSlice({
  name: 'examV3ExamReservationSettings',
  initialState,
  reducers: {
    fetchSettingsRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchSettingsSuccess: (
      state,
      {
        payload,
      }: PayloadAction<Partial<ExamV3ExamReservationConfiguration> | null>
    ) => {
      finishedLoadingSuccess(state);
      if (payload != null) {
        state.config = payload;
      }
    },
    fetchSettingsFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setConfig: (
      state,
      { payload }: PayloadAction<ExamV3ExamReservationConfiguration | null>
    ) => {
      state.config = payload;
    },
    examV3ExamReservationSettingOnChange: (
      state,
      { payload }: PayloadAction<{ path: string; value: any }>
    ) => {
      if (!state.config) {
        state.config = {};
      }
      set(state.config, `${payload.path}`, payload.value);
    },
    examV3ExamReservationSettingOnClear: (
      state,
      { payload }: PayloadAction<{ path: string }>
    ) => {
      let newConfig = omit(state.config, [payload.path]);
      const parentPath = payload.path.split('.').slice(0, -1).join('.');

      if (Object.keys(get(newConfig, parentPath) ?? {}).length === 0) {
        newConfig = omit(newConfig, [parentPath]);
      }

      state.config = newConfig;
    },
    setErrors: (
      state,
      { payload }: PayloadAction<Record<string, z.ZodIssue> | null>
    ) => {
      if (payload === null) {
        state.hasErrors = false;
        state.errors = null;
      } else {
        state.hasErrors = true;
        state.errors = payload;
      }
    },
  },
});

export const {
  examV3ExamReservationSettingOnChange,
  examV3ExamReservationSettingOnClear,
} = slice.actions;

/// Selectors
export const examV3ExamReservationSettingsSelector = (state: TRootState) =>
  state.examV3ExamReservationSettings.config;

export const examV3ExamReservationSettingsLoadingSelector = (
  state: TRootState
) => state.examV3ExamReservationSettings.loading;

export const examV3ExamReservationSettingsErrorsSelector = (
  state: TRootState
) => state.examV3ExamReservationSettings.errors;

/// Thunks

export const fetchExamV3ExamReservationSettings = createAppAsyncThunk(
  'examV3Settings/examV3ExamReservationSettingsFetchSettings',
  async (_, { dispatch }) => {
    try {
      const result = await api.get({
        endpoint: `/apps/TE_EXAM_V3/exam-reservation-display-settings`,
      });
      const configWithListIds = configFromNetwork(
        result.examReservationDisplaySettings
      );
      dispatch(slice.actions.fetchSettingsSuccess(configWithListIds));
    } catch (e) {
      console.error(e);
    }
  }
);

export const examV3ExamReservationSettingsHandleValidationFailure =
  createAppAsyncThunk(
    'examV3Settings/examV3EditExportTemplateHandleValidationFailure',
    (
      {
        validationResult,
        sendNotification,
      }: { validationResult: any; sendNotification: boolean },
      { dispatch }
    ) => {
      const errorMap = keyBy(validationResult.error.issues, 'path');
      dispatch(slice.actions.setErrors(errorMap));
      if (sendNotification) {
        errorNotification(
          'Configuration could not be validated',
          ['Please verify fields marked in red'].concat(
            validationResult.error.issues.map((error) => error.message)
          )
        );
      }
      return;
    }
  );

export const saveExamV3ExamReservationSettings = createAppAsyncThunk(
  'examV3Settings/saveExamReservationSettings',
  async (_, { getState, dispatch }) => {
    const { _id, id, createdBy, createdAt, updatedAt, updatedBy, ...config } =
      getState().examV3ExamReservationSettings.config;

    const configWithoutIds = configToNetwork(config);

    const validationResult = validateExamReservationSettings(configWithoutIds);

    if (!validationResult.success) {
      dispatch(
        examV3ExamReservationSettingsHandleValidationFailure({
          validationResult,
          sendNotification: true,
        })
      );
      return;
    } else {
      dispatch(slice.actions.setErrors(null));
    }

    try {
      const result = await api.patch({
        endpoint: `/apps/TE_EXAM_V3/exam-reservation-display-settings`,
        data: configWithoutIds,
      });
      const resultWithIds = configFromNetwork(
        result.examReservationDisplaySettings
      );
      dispatch(slice.actions.fetchSettingsSuccess(resultWithIds));
    } catch (e) {}
  }
);

export const examV3ExamReservationSettingsValidate = createAppAsyncThunk(
  'examV3Settings/examV3ExamReservationSettingsValidate',
  async (sendNotification: boolean, { dispatch, getState }) => {
    const config = getState().examV3ExamReservationSettings.config;
    const validationResult = await validateExamReservationSettingsAsync(config);

    if (!validationResult.success) {
      dispatch(
        examV3ExamReservationSettingsHandleValidationFailure({
          validationResult,
          sendNotification,
        })
      );
      return;
    } else {
      dispatch(slice.actions.setErrors(null));
    }
  }
);

const configToNetwork = (
  config: Partial<ExamV3ExamReservationConfiguration>
): Partial<ExamV3ExamReservationConfiguration> => {
  const copy = _.cloneDeep(config);

  copy?.fields?.forEach((field) => {
    delete field._id;
    delete field.id;
  });

  copy?.information?.forEach((block) => {
    delete block._id;
    delete block.id;
  });

  return copy;
};

const configFromNetwork = (
  config: Partial<ExamV3ExamReservationConfiguration>
) => {
  const copy = _.cloneDeep(config);

  if (Object.keys(copy.examLabel ?? {}).length === 0) {
    delete copy.examLabel;
  }
  if (Object.keys(copy.examType ?? {}).length === 0) {
    delete copy.examType;
  }

  if (Object.keys(copy.location?.room ?? {}).length === 0) {
    delete copy.location?.room;
  }
  if (Object.keys(copy.location?.building ?? {}).length === 0) {
    delete copy.location?.building;
  }
  if (Object.keys(copy.location?.campus ?? {}).length === 0) {
    delete copy.location?.campus;
  }

  copy?.fields?.forEach((field) => {
    if (field._id) {
      field.id = field._id;
    }
  });

  copy?.information?.forEach((block) => {
    if (block._id) {
      block.id = block._id;
    }
  });

  return copy;
};

export default slice.reducer;
